import React, { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, Transition } from '@headlessui/react';
import {
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { cn } from '../../utils/cn';

/**
 * Tafwija calendar date picker — design-system styled, RTL-aware.
 *
 * Props:
 *   value           Date | string         currently selected date
 *   onChange(date)  fn                     receives a JS Date
 *   minDate         Date                   earliest selectable (default: today)
 *   isDateDisabled  (date: Date) => bool   optional extra disabling (e.g. range)
 *   placeholder     string
 *
 * Designed to be a drop-in for the booking page's MUI KeyboardDatePicker,
 * matching its `value` / `onChange` / `minDate` / `shouldDisableDate` contract.
 */

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function sameDay(a, b) {
  return (
    a &&
    b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export default function DatePicker({
  value,
  onChange,
  minDate,
  isDateDisabled,
  placeholder,
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ar' ? 'ar' : i18n.language === 'en' ? 'en' : 'fr';

  const selected = value ? startOfDay(new Date(value)) : null;
  const min = minDate ? startOfDay(minDate) : startOfDay(new Date());

  // Month currently shown in the calendar grid.
  const [view, setView] = useState(() => {
    const base = selected || min || new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  const monthLabel = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(view),
    [view, locale]
  );

  // Weekday short names, starting Monday (Mon..Sun).
  const weekdays = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short' });
    // 2024-01-01 is a Monday — build Mon..Sun.
    return Array.from({ length: 7 }).map((_, i) =>
      fmt.format(new Date(2024, 0, 1 + i))
    );
  }, [locale]);

  // Build the 6-row grid of dates for the current view month.
  const cells = useMemo(() => {
    const year = view.getFullYear();
    const month = view.getMonth();
    const first = new Date(year, month, 1);
    // JS getDay: 0=Sun..6=Sat → shift so Monday is column 0.
    const lead = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const arr = [];
    for (let i = 0; i < lead; i += 1) arr.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) arr.push(new Date(year, month, d));
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [view]);

  const displayValue = selected
    ? new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }).format(selected)
    : '';

  const isDisabled = (date) => {
    if (!date) return true;
    if (min && date < min) return true;
    if (isDateDisabled && isDateDisabled(date)) return true;
    return false;
  };

  const goMonth = (delta) =>
    setView((v) => new Date(v.getFullYear(), v.getMonth() + delta, 1));

  return (
    <Popover className="relative">
      {({ close }) => (
        <>
          <Popover.Button
            className={cn(
              'flex w-full items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-start text-sm transition-colors focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100',
              selected ? 'text-ink' : 'text-ink-subtle'
            )}
          >
            <span className="truncate">{displayValue || placeholder || t('booking.chooseDate')}</span>
            <CalendarDaysIcon className="h-4 w-4 shrink-0 text-brand-500" />
          </Popover.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-150"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel className="absolute z-40 mt-2 w-[300px] rounded-2xl border border-slate-100 bg-white p-4 shadow-elevated">
              {/* Month nav */}
              <div className="mb-3 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => goMonth(-1)}
                  className="flex h-8 w-8 items-center justify-center rounded-full text-ink-muted transition-colors hover:bg-surface-muted hover:text-brand-500"
                  aria-label="Previous month"
                >
                  <ChevronLeftIcon className="h-4 w-4 rtl:rotate-180" />
                </button>
                <span className="font-display text-sm font-semibold capitalize text-ink">
                  {monthLabel}
                </span>
                <button
                  type="button"
                  onClick={() => goMonth(1)}
                  className="flex h-8 w-8 items-center justify-center rounded-full text-ink-muted transition-colors hover:bg-surface-muted hover:text-brand-500"
                  aria-label="Next month"
                >
                  <ChevronRightIcon className="h-4 w-4 rtl:rotate-180" />
                </button>
              </div>

              {/* Weekday header */}
              <div className="mb-1 grid grid-cols-7 gap-1 text-center">
                {weekdays.map((w, i) => (
                  <span key={i} className="text-[11px] font-medium uppercase text-ink-subtle">
                    {w}
                  </span>
                ))}
              </div>

              {/* Day grid */}
              <div className="grid grid-cols-7 gap-1">
                {cells.map((date, i) => {
                  if (!date) return <span key={i} />;
                  const disabled = isDisabled(date);
                  const isSel = sameDay(date, selected);
                  const isToday = sameDay(date, startOfDay(new Date()));
                  return (
                    <button
                      key={i}
                      type="button"
                      disabled={disabled}
                      onClick={() => {
                        onChange(date);
                        close();
                      }}
                      className={cn(
                        'flex h-9 w-9 items-center justify-center rounded-full text-sm transition-colors',
                        isSel
                          ? 'bg-brand-500 font-semibold text-white'
                          : disabled
                          ? 'cursor-not-allowed text-slate-300'
                          : 'text-ink hover:bg-brand-50 hover:text-brand-600',
                        !isSel && isToday && 'font-semibold text-brand-500'
                      )}
                    >
                      {date.getDate()}
                    </button>
                  );
                })}
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}
